import { useState, useEffect } from "react";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UploadSimple, VideoCamera, Image as ImageIcon, LinkSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function UploadDialog({ open, onOpenChange, campaigns, onCreated, resubmitFor, preselectCampaignId }) {
  const [campaignId, setCampaignId] = useState("");
  const [mediaType, setMediaType] = useState("image");
  const [caption, setCaption] = useState("");
  const [externalUrl, setExternalUrl] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && resubmitFor) {
      setCampaignId(resubmitFor.campaign_id);
      setMediaType(resubmitFor.media_type || "image");
      setCaption(resubmitFor.caption || "");
      setExternalUrl(resubmitFor.external_url || "");
    } else if (open) {
      // Prefer explicit preselected id (e.g. clicked from a campaign card), else first campaign.
      const initial = preselectCampaignId && campaigns?.some((c) => c.id === preselectCampaignId)
        ? preselectCampaignId
        : (campaigns?.[0]?.id || "");
      setCampaignId(initial);
      setMediaType("image");
      setCaption("");
      setExternalUrl("");
      setFile(null);
    }
  }, [open, resubmitFor, campaigns, preselectCampaignId]);

  const submit = async () => {
    if (!campaignId) return toast.error("Choose a campaign");
    if (!caption.trim()) return toast.error("Add a caption");
    if (mediaType !== "link" && !file && !resubmitFor?.file_id) return toast.error("Attach a file");
    if (mediaType === "link" && !externalUrl.trim()) return toast.error("Add a link");

    setBusy(true);
    try {
      let fileId = resubmitFor?.file_id || null;
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        fileId = data.id;
      }
      const body = {
        campaign_id: campaignId,
        caption,
        external_url: externalUrl,
        file_id: fileId,
        media_type: mediaType,
      };
      if (resubmitFor) {
        await api.post(`/submissions/${resubmitFor.id}/resubmit`, body);
        toast.success("Resubmitted for review");
      } else {
        await api.post("/submissions", body);
        toast.success("Submitted for review");
      }
      onCreated && onCreated();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-black border-white/10 text-zinc-50 sm:max-w-lg" data-testid="upload-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading font-black text-2xl">
            {resubmitFor ? "Resubmit content" : "Upload new content"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {!resubmitFor && (
            <div>
              <Label className="text-zinc-300">Campaign</Label>
              <Select value={campaignId} onValueChange={setCampaignId}>
                <SelectTrigger data-testid="upload-campaign-select" className="bg-zinc-900 border-white/10 mt-1.5 h-11">
                  <SelectValue placeholder="Choose a campaign" />
                </SelectTrigger>
                <SelectContent className="bg-black border-white/10 text-zinc-100">
                  {campaigns?.length ? campaigns.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  )) : <div className="p-3 text-sm text-zinc-500">No campaigns assigned yet</div>}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <Label className="text-zinc-300 mb-2 block">Media type</Label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { v: "image", label: "Image", Icon: ImageIcon },
                { v: "video", label: "Video", Icon: VideoCamera },
                { v: "link", label: "Link only", Icon: LinkSimple },
              ].map(({ v, label, Icon }) => (
                <button
                  key={v}
                  type="button"
                  data-testid={`media-type-${v}`}
                  onClick={() => setMediaType(v)}
                  className={`p-3 rounded-lg border text-sm transition-colors ${
                    mediaType === v
                      ? "bg-yellow-400/10 border-yellow-400 text-yellow-300"
                      : "bg-zinc-900 border-white/10 text-zinc-400"
                  }`}
                >
                  <Icon size={20} weight="duotone" className="mx-auto mb-1" />
                  {label}
                </button>
              ))}
            </div>
          </div>

          {mediaType !== "link" && (
            <div>
              <Label className="text-zinc-300">File {resubmitFor && "(leave empty to keep existing)"}</Label>
              <label
                htmlFor="upload-file"
                className="mt-1.5 flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-white/10 bg-zinc-900/50 p-6 cursor-pointer hover:border-yellow-400/40 transition-colors"
              >
                <UploadSimple size={28} weight="duotone" className="text-zinc-500 mb-2" />
                <span className="text-sm text-zinc-300">
                  {file ? file.name : "Click to select file"}
                </span>
                <span className="text-xs text-zinc-500 mt-1">Max 100MB · MP4, MOV, JPG, PNG, WEBP</span>
                <input
                  id="upload-file"
                  type="file"
                  data-testid="upload-file-input"
                  accept={mediaType === "video" ? "video/*" : "image/*"}
                  onChange={(e) => setFile(e.target.files[0])}
                  className="hidden"
                />
              </label>
            </div>
          )}

          <div>
            <Label className="text-zinc-300">External URL (optional — IG, TikTok, YouTube)</Label>
            <Input
              data-testid="upload-external-url"
              value={externalUrl}
              onChange={(e) => setExternalUrl(e.target.value)}
              placeholder="https://instagram.com/p/…"
              className="bg-zinc-900 border-white/10 mt-1.5 h-11"
            />
          </div>

          <div>
            <Label className="text-zinc-300">Caption</Label>
            <Textarea
              data-testid="upload-caption-input"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Post caption or content notes…"
              className="bg-zinc-900 border-white/10 mt-1.5 min-h-24"
            />
          </div>

          <Button
            onClick={submit}
            disabled={busy}
            data-testid="upload-submit-button"
            className="w-full bg-yellow-400 hover:bg-yellow-300 text-black font-bold h-11"
          >
            {busy ? "Submitting…" : resubmitFor ? "Resubmit for review" : "Submit for review"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
